import { z } from 'zod';
import { BREATHWORK_LIMITS, BUILT_IN_BREATHWORK_KEYS } from '../coaching/breathwork';
import {
  BUILT_IN_COACH_PROMPT_KEYS,
  COACH_PROMPT_CATEGORIES,
  COACH_PROMPT_LIMITS,
} from '../coaching/coach-prompts';
import { COACHING_PROGRAM_LIMITS, COACHING_PROGRAM_MEDIA_KINDS } from '../coaching/programs';
import {
  COACHING_ACTION_STATUSES,
  COACHING_BLOCKER_KEYS,
  COACHING_BREATHWORK_OUTCOMES,
  COACHING_EVENT_NAMES,
  COACHING_FEELINGS,
  COACHING_FOCUS_KEYS,
  COACHING_FREQUENCY_CATEGORIES,
  COACHING_GOAL_STATUSES,
  COACHING_MILESTONE_STATUSES,
  COACHING_NEED_KEYS,
  COACHING_PLAN_PERIODS,
  COACHING_PLAN_TIERS,
  COACHING_PRACTICES,
  COACHING_READING_KINDS,
  COACHING_RITUAL_MODULE_KEYS,
  COACHING_STYLE_KEYS,
  COACHING_THREE_SIX_NINE_SESSIONS,
  COACHING_TIERS,
  COACHING_VISION_BOARD_LIMIT,
} from '../coaching/constants';
import { isCalendarDate } from '../accounting';
import { isValidTimeZone } from '../coaching/dates';
import { positiveMoneySchema, uuidSchema } from './common';
import { FUTURE_SELF_ANSWER_MAX, FUTURE_SELF_HORIZONS } from '../coaching/future-self';

/**
 * Request schemas for the `/coaching/*` API (Miracles Protocol). Written out
 * field by field rather than derived with `.partial()`: the calendar-date
 * refinements are ZodEffects, and deriving from an object that carries them
 * degrades zod's inferred types (see zod/common.ts).
 */

export const coachingCalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
  .refine(isCalendarDate, 'must be a real date');

export const coachingClockTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM (24-hour)');

export const coachingTimeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, 'unknown timezone');

export const coachingAssessmentSchema = z.object({
  satisfaction: z.number().int().min(1).max(10),
  confidence: z.number().int().min(1).max(10),
  blockers: z.array(z.enum(COACHING_BLOCKER_KEYS)).max(COACHING_BLOCKER_KEYS.length),
});
export type CoachingAssessment = z.infer<typeof coachingAssessmentSchema>;

export const coachingRitualPrefsSchema = z.object({
  minutes: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]),
  modules: z.array(z.enum(COACHING_RITUAL_MODULE_KEYS)).max(COACHING_RITUAL_MODULE_KEYS.length),
});
export type CoachingRitualPrefs = z.infer<typeof coachingRitualPrefsSchema>;

/** Reminder preferences (SC-42). Stored now; delivery arrives with the phone app. */
export const coachingNotificationPrefsSchema = z.object({
  morningReminder: coachingClockTimeSchema.nullable(),
  eveningReminder: coachingClockTimeSchema.nullable(),
  coachNudges: z.boolean(),
});
export type CoachingNotificationPrefs = z.infer<typeof coachingNotificationPrefsSchema>;

/** PUT /coaching/profile: every field optional; absent = leave as stored. */
export const coachingProfilePatchSchema = z.object({
  firstName: z.string().trim().min(1).max(100).optional(),
  birthDate: coachingCalendarDateSchema.nullable().optional(),
  birthTime: coachingClockTimeSchema.nullable().optional(),
  birthTimeKnown: z.boolean().optional(),
  birthPlace: z.string().trim().max(200).nullable().optional(),
  birthLat: z.number().min(-90).max(90).nullable().optional(),
  birthLng: z.number().min(-180).max(180).nullable().optional(),
  timezone: coachingTimeZoneSchema.optional(),
  focusCategory: z.enum(COACHING_FOCUS_KEYS).nullable().optional(),
  assessment: coachingAssessmentSchema.optional(),
  ritualPrefs: coachingRitualPrefsSchema.optional(),
  coachingStyle: z.enum(COACHING_STYLE_KEYS).nullable().optional(),
  coachingNeeds: z.array(z.enum(COACHING_NEED_KEYS)).max(COACHING_NEED_KEYS.length).optional(),
  notificationPrefs: coachingNotificationPrefsSchema.optional(),
});
export type CoachingProfilePatch = z.infer<typeof coachingProfilePatchSchema>;

export const coachingGoalCreateSchema = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(2000).optional(),
  category: z.enum(COACHING_FOCUS_KEYS).optional(),
  targetDate: coachingCalendarDateSchema.nullable().optional(),
  reasonWhy: z.string().trim().max(1000).optional(),
  isPrimary: z.boolean().optional(),
});
export type CoachingGoalCreate = z.infer<typeof coachingGoalCreateSchema>;

export const coachingGoalUpdateSchema = z.object({
  title: z.string().trim().min(3).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  category: z.enum(COACHING_FOCUS_KEYS).nullable().optional(),
  targetDate: coachingCalendarDateSchema.nullable().optional(),
  reasonWhy: z.string().trim().max(1000).nullable().optional(),
  /** Promote only: a goal stops being primary when another one is promoted. */
  isPrimary: z.literal(true).optional(),
  status: z.enum(COACHING_GOAL_STATUSES).optional(),
  progressPercent: z.number().int().min(0).max(100).optional(),
});
export type CoachingGoalUpdate = z.infer<typeof coachingGoalUpdateSchema>;

export const coachingMilestoneCreateSchema = z.object({
  title: z.string().trim().min(2).max(200),
  targetDate: coachingCalendarDateSchema.nullable().optional(),
});
export type CoachingMilestoneCreate = z.infer<typeof coachingMilestoneCreateSchema>;

export const coachingMilestoneUpdateSchema = z.object({
  title: z.string().trim().min(2).max(200).optional(),
  targetDate: coachingCalendarDateSchema.nullable().optional(),
  status: z.enum(COACHING_MILESTONE_STATUSES).optional(),
});
export type CoachingMilestoneUpdate = z.infer<typeof coachingMilestoneUpdateSchema>;

export const coachingActionCreateSchema = z.object({
  title: z.string().trim().min(2).max(200),
  notes: z.string().trim().max(1000).optional(),
  goalId: z.string().uuid().nullable().optional(),
  priority: z.number().int().min(1).max(3).optional(),
  estimatedMinutes: z.number().int().min(1).max(600).nullable().optional(),
  /** Defaults to the member's local today. */
  scheduledDate: coachingCalendarDateSchema.optional(),
});
export type CoachingActionCreate = z.infer<typeof coachingActionCreateSchema>;

export const coachingActionUpdateSchema = z.object({
  title: z.string().trim().min(2).max(200).optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  goalId: z.string().uuid().nullable().optional(),
  priority: z.number().int().min(1).max(3).optional(),
  estimatedMinutes: z.number().int().min(1).max(600).nullable().optional(),
  status: z.enum(COACHING_ACTION_STATUSES).optional(),
  /** A new date reschedules the action and records where it came from. */
  scheduledDate: coachingCalendarDateSchema.optional(),
});
export type CoachingActionUpdate = z.infer<typeof coachingActionUpdateSchema>;

export const coachingPracticeSchema = z.object({ practice: z.enum(COACHING_PRACTICES) });

export const coachingEventSchema = z.object({
  name: z.enum(COACHING_EVENT_NAMES),
  props: z
    .record(
      z.string().max(40),
      z.union([z.string().max(200), z.number().finite(), z.boolean(), z.null()]),
    )
    .refine((p) => Object.keys(p).length <= 20, 'at most 20 props')
    .optional(),
});
export type CoachingEvent = z.infer<typeof coachingEventSchema>;

export const coachingGeocodeSchema = z.object({ query: z.string().trim().min(2).max(200) });

/** POST /coaching/rituals/morning: progress through today's ritual, sent after each step. */
export const coachingMorningRitualSchema = z
  .object({
    totalSteps: z.number().int().min(1).max(10),
    stepsCompleted: z.number().int().min(0).max(10),
    completed: z.boolean().default(false),
    intention: z.string().trim().max(280).optional(),
    gratitude: z.array(z.string().trim().min(1).max(200)).max(3).optional(),
    modules: z
      .array(z.enum(COACHING_RITUAL_MODULE_KEYS))
      .max(COACHING_RITUAL_MODULE_KEYS.length)
      .optional(),
    /** Practices completed in the step just finished (affirmation, breathwork…). */
    practices: z.array(z.enum(COACHING_PRACTICES)).max(4).optional(),
  })
  .refine((v) => v.stepsCompleted <= v.totalSteps, {
    message: 'stepsCompleted cannot exceed totalSteps',
    path: ['stepsCompleted'],
  });
export type CoachingMorningRitual = z.infer<typeof coachingMorningRitualSchema>;

/** POST /coaching/rituals/evening. */
export const coachingEveningReviewSchema = z.object({
  wins: z.array(z.string().trim().min(1).max(200)).min(1).max(3),
  challenge: z.string().trim().max(500).optional(),
  lesson: z.string().trim().max(500).optional(),
  gratitude: z.array(z.string().trim().min(1).max(200)).max(3).optional(),
  tomorrowPriority: z.string().trim().min(2).max(200).optional(),
  mood: z.number().int().min(1).max(5).optional(),
  feeling: z.enum(COACHING_FEELINGS).optional(),
  /** Also plan `tomorrowPriority` as tomorrow's #1 action. */
  addTomorrowAction: z.boolean().optional(),
});
export type CoachingEveningReview = z.infer<typeof coachingEveningReviewSchema>;

/** POST /coaching/journal. `evening` entries come from the evening review only. */
export const coachingJournalCreateSchema = z.object({
  kind: z.enum(['free', 'prompt', 'gratitude']).default('free'),
  prompt: z.string().trim().max(300).optional(),
  content: z.string().trim().min(1).max(4000),
  mood: z.number().int().min(1).max(5).optional(),
  tags: z.array(z.string().trim().min(1).max(30)).max(10).optional(),
  goalId: z.string().uuid().nullable().optional(),
});
export type CoachingJournalCreate = z.infer<typeof coachingJournalCreateSchema>;

export const coachingJournalListSchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  before: z.string().datetime({ offset: true }).optional(),
});

export const coachingAffirmationCreateSchema = z.object({
  text: z.string().trim().min(3).max(220),
  inRitual: z.boolean().optional(),
});
export type CoachingAffirmationCreate = z.infer<typeof coachingAffirmationCreateSchema>;

export const coachingAffirmationUpdateSchema = z.object({
  text: z.string().trim().min(3).max(220).optional(),
  favorite: z.boolean().optional(),
  inRitual: z.boolean().optional(),
});
export type CoachingAffirmationUpdate = z.infer<typeof coachingAffirmationUpdateSchema>;

export const coachingAffirmationGenerateSchema = z.object({
  goalId: z.string().uuid().optional(),
  count: z.number().int().min(1).max(3).default(3),
});

export const coachingSuggestActionsSchema = z.object({
  goalId: z.string().uuid(),
  day: coachingCalendarDateSchema.optional(),
});

// ── Phase 3: plans, content and entitlements (admin surface) ────────────────

/**
 * The seconds of one breath and the rounds in a session, as a spreadable shape
 * (a function, so every schema gets its own instances). Inhale and exhale need
 * at least a second; a hold of zero is skipped by the session timer.
 */
function breathworkTimingShape() {
  const seconds = z.number().int().min(0).max(BREATHWORK_LIMITS.phaseMaxSeconds);
  return {
    inhale: seconds.min(1),
    holdIn: seconds,
    exhale: seconds.min(1),
    holdOut: seconds,
    cycles: z.number().int().min(1).max(BREATHWORK_LIMITS.cyclesMax),
  };
}

/**
 * PUT /admin/coaching/plans. Only the cells the admin edited are sent, so an
 * absent (tier, period) is left exactly as it is rather than deactivated —
 * unlike the creator plan editor, which owns the whole set.
 *
 * `productId` binds an existing approved virtual product; omitting it with a
 * `price` mints the hidden product. Sending neither only flips `active`.
 */
export const coachingPlansUpdateSchema = z.object({
  plans: z
    .array(
      z.object({
        tier: z.enum(COACHING_PLAN_TIERS),
        period: z.enum(COACHING_PLAN_PERIODS),
        productId: uuidSchema.optional(),
        price: positiveMoneySchema.optional(),
        active: z.boolean().default(true),
      }),
    )
    .min(1)
    .max(COACHING_PLAN_TIERS.length * COACHING_PLAN_PERIODS.length)
    .refine(
      (rows) => new Set(rows.map((r) => `${r.tier}:${r.period}`)).size === rows.length,
      'each tier and period may appear once',
    ),
});
export type CoachingPlansUpdate = z.infer<typeof coachingPlansUpdateSchema>;

/**
 * POST /admin/coaching/content — a TYPED body per kind, never raw JSON. The
 * `reading_copy` key (`<readingKind>:<name>`) is built server-side so an admin
 * can never author a key no reader looks up.
 */
export const coachingContentCreateSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('affirmation'),
    body: z.string().trim().min(3).max(300),
    category: z.enum(COACHING_FOCUS_KEYS).nullable().optional(),
    premiumLevel: z.enum(COACHING_TIERS).default('free'),
    sortOrder: z.number().int().min(0).max(10000).optional(),
    active: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('prompt'),
    body: z.string().trim().min(3).max(300),
    premiumLevel: z.enum(COACHING_TIERS).default('free'),
    sortOrder: z.number().int().min(0).max(10000).optional(),
    active: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('reading_copy'),
    readingKind: z.enum(COACHING_READING_KINDS),
    /** The entry being overridden: a sign, a yoga, a nakshatra, a graha, or 1-12. */
    name: z.string().trim().min(1).max(60),
    body: z.string().trim().min(3).max(2000),
    active: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('breathwork'),
    /**
     * A built-in pattern's key OVERRIDES that pattern (saved again, it edits
     * the override; inactive, it hides the built-in). No key adds a pattern of
     * the tenant's own. A key no built-in carries is refused by the enum, so
     * an override can never point at nothing.
     */
    key: z.enum(BUILT_IN_BREATHWORK_KEYS).optional(),
    title: z.string().trim().min(3).max(80),
    body: z.string().trim().min(3).max(300),
    outcome: z.enum(COACHING_BREATHWORK_OUTCOMES),
    ...breathworkTimingShape(),
    premiumLevel: z.enum(COACHING_TIERS).default('free'),
    sortOrder: z.number().int().min(0).max(10000).optional(),
    active: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('coach_prompt'),
    /** A built-in prompt's key overrides it (inactive hides it); no key adds one of the tenant's own. */
    key: z.enum(BUILT_IN_COACH_PROMPT_KEYS).optional(),
    category: z.enum(COACH_PROMPT_CATEGORIES),
    title: z.string().trim().min(3).max(COACH_PROMPT_LIMITS.titleMax),
    /** What lands in the member's composer, written in their voice. */
    body: z.string().trim().min(10).max(COACH_PROMPT_LIMITS.bodyMax),
    premiumLevel: z.enum(COACHING_TIERS).default('free'),
    sortOrder: z.number().int().min(0).max(10000).optional(),
    active: z.boolean().optional(),
  }),
]);
export type CoachingContentCreate = z.infer<typeof coachingContentCreateSchema>;

/** PATCH /admin/coaching/content/:id. `kind` and `key` are immutable. */
export const coachingContentPatchSchema = z.object({
  body: z.string().trim().min(3).max(2000).optional(),
  /** Affirmation rows only; breathwork and audio carry their own category below. */
  category: z.enum(COACHING_FOCUS_KEYS).nullable().optional(),
  premiumLevel: z.enum(COACHING_TIERS).optional(),
  sortOrder: z.number().int().min(0).max(10000).optional(),
  active: z.boolean().optional(),
  /** Breathwork, audio and coach-prompt rows. */
  title: z.string().trim().min(3).max(80).optional(),
  /** Applied only to a coach-prompt row (its category is not a focus key). */
  coachPrompt: z
    .object({ category: z.enum(COACH_PROMPT_CATEGORIES) })
    .partial()
    .optional(),
  /** Applied only to a breathwork row. */
  breathwork: z
    .object({
      outcome: z.enum(COACHING_BREATHWORK_OUTCOMES),
      ...breathworkTimingShape(),
    })
    .partial()
    .optional(),
  /** Applied only to an audio row. */
  audio: z
    .object({
      category: z.enum(COACHING_FREQUENCY_CATEGORIES),
      frequencyLabel: z.string().trim().max(40).nullable(),
      durationSeconds: z
        .number()
        .int()
        .min(1)
        .max(4 * 3600)
        .nullable(),
    })
    .partial()
    .optional(),
});
export type CoachingContentPatch = z.infer<typeof coachingContentPatchSchema>;

/**
 * The text fields of POST /admin/coaching/audio, parsed from the multipart
 * parts beside the file (the route drops empty strings first, so an optional
 * field left blank is simply absent).
 */
export const coachingAudioFieldsSchema = z.object({
  title: z.string().trim().min(3).max(80),
  body: z.string().trim().max(300).default(''),
  category: z.enum(COACHING_FREQUENCY_CATEGORIES),
  /** "528 Hz", "Theta binaural": shown beside the title, never parsed. */
  frequencyLabel: z.string().trim().max(40).optional(),
  /** Read from the file by the admin's browser before upload. */
  durationSeconds: z.coerce
    .number()
    .int()
    .min(1)
    .max(4 * 3600)
    .optional(),
  premiumLevel: z.enum(COACHING_TIERS).default('free'),
  sortOrder: z.coerce.number().int().min(0).max(10000).optional(),
});
export type CoachingAudioFields = z.infer<typeof coachingAudioFieldsSchema>;

// ── Phase 4: the Align and Manifest tools (member surface) ──────────────────

/** PUT /coaching/369: save one session's lines (the route checks the count per session). */
export const coachingThreeSixNineSaveSchema = z.object({
  affirmation: z.string().trim().min(3).max(220),
  session: z.enum(COACHING_THREE_SIX_NINE_SESSIONS),
  lines: z.array(z.string().trim().min(1).max(220)).min(1).max(9),
});
export type CoachingThreeSixNineSave = z.infer<typeof coachingThreeSixNineSaveSchema>;

/** The text parts of POST /coaching/vision-board, beside the image. */
export const coachingVisionBoardFieldsSchema = z.object({
  caption: z.string().trim().max(200).optional(),
  goalId: z.string().uuid().optional(),
});

export const coachingVisionBoardPatchSchema = z.object({
  caption: z.string().trim().max(200).nullable().optional(),
  goalId: z.string().uuid().nullable().optional(),
});
export type CoachingVisionBoardPatch = z.infer<typeof coachingVisionBoardPatchSchema>;

/** PUT /coaching/vision-board/order: every item id, in the new order. */
export const coachingVisionBoardOrderSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(COACHING_VISION_BOARD_LIMIT),
});

export const coachingWeeklyReviewQuerySchema = z.object({
  /** A Monday; absent = the week the member is most likely closing. */
  weekStart: coachingCalendarDateSchema.optional(),
});

const weeklyAnswer = z.string().trim().max(600);

/** POST /coaching/review/weekly. The win and next week's focus are required; the rest may be blank. */
export const coachingWeeklyReviewSchema = z.object({
  weekStart: coachingCalendarDateSchema.optional(),
  answers: z.object({
    biggestWin: weeklyAnswer.min(1),
    progressSource: weeklyAnswer.default(''),
    heldBack: weeklyAnswer.default(''),
    learned: weeklyAnswer.default(''),
    nextWeek: weeklyAnswer.min(1),
  }),
});
export type CoachingWeeklyReview = z.infer<typeof coachingWeeklyReviewSchema>;

/**
 * Admin grant. `addDays` STACKS from the member's current expiry IN THAT TIER
 * (an admin extending someone must never shorten them by accident); `endsAt`
 * is absolute, because "open it until this date" is a decision.
 */
export const coachingEntitlementGrantSchema = z
  .object({
    tier: z.enum(COACHING_PLAN_TIERS),
    endsAt: z.coerce.date().optional(),
    addDays: z.number().int().min(1).max(3650).optional(),
    note: z.string().trim().max(200).optional(),
  })
  .refine((v) => !!v.endsAt !== (typeof v.addDays === 'number'), {
    message: 'provide exactly one of endsAt or addDays',
  });
export type CoachingEntitlementGrant = z.infer<typeof coachingEntitlementGrantSchema>;

export const coachingEntitlementRevokeSchema = z.object({
  note: z.string().trim().max(200).optional(),
});

/**
 * One miracle win. `amount` is the member's own private note of what the win
 * was worth and is always optional; it is a decimal string so no money ever
 * rides a float. Nothing sums it.
 */
export const coachingWinCreateSchema = z.object({
  note: z.string().trim().min(2).max(500),
  amount: z
    .union([z.string().trim(), z.number()])
    .nullish()
    .transform((v) => (v === null || v === undefined || v === '' ? null : String(v)))
    .refine((v) => v === null || /^\d{1,12}(\.\d{1,2})?$/.test(v), {
      message: 'amount must be a positive number with at most two decimals',
    }),
});
export type CoachingWinCreate = z.infer<typeof coachingWinCreateSchema>;

/** One horizon's answers. A PATCH: absent keys are left alone, empty ones cleared. */
export const coachingFutureSelfSaveSchema = z.object({
  horizon: z.enum(FUTURE_SELF_HORIZONS),
  answers: z.record(z.string().max(FUTURE_SELF_ANSWER_MAX)),
});
export type CoachingFutureSelfSave = z.infer<typeof coachingFutureSelfSaveSchema>;

export const coachingAlignmentTodoUpdateSchema = z.object({ done: z.boolean() });
export type CoachingAlignmentTodoUpdate = z.infer<typeof coachingAlignmentTodoUpdateSchema>;

/** The five answers an aura reading is computed from, each 1-5. */
export const coachingAuraCheckInSchema = z.object({
  mood: z.number().int().min(1).max(5),
  energy: z.number().int().min(1).max(5),
  confidence: z.number().int().min(1).max(5),
  stress: z.number().int().min(1).max(5),
  focus: z.number().int().min(1).max(5),
});
export type CoachingAuraCheckIn = z.infer<typeof coachingAuraCheckInSchema>;

// ── guided programs (batch 3 of the Manifest AI gap work) ──────────────────

const programTitle = z.string().trim().min(3).max(COACHING_PROGRAM_LIMITS.titleMax);

/** POST /admin/coaching/programs. Days come separately through the PUT below. */
export const coachingProgramCreateSchema = z.object({
  title: programTitle,
  tagline: z.string().trim().max(COACHING_PROGRAM_LIMITS.taglineMax).default(''),
  description: z.string().trim().max(COACHING_PROGRAM_LIMITS.descriptionMax).default(''),
  premiumLevel: z.enum(COACHING_TIERS).default('free'),
  active: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10000).optional(),
});
export type CoachingProgramCreate = z.infer<typeof coachingProgramCreateSchema>;

/** PATCH /admin/coaching/programs/:id. `key` is immutable; the day count follows the days. */
export const coachingProgramPatchSchema = z.object({
  title: programTitle.optional(),
  tagline: z.string().trim().max(COACHING_PROGRAM_LIMITS.taglineMax).optional(),
  description: z.string().trim().max(COACHING_PROGRAM_LIMITS.descriptionMax).optional(),
  premiumLevel: z.enum(COACHING_TIERS).optional(),
  active: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10000).optional(),
});
export type CoachingProgramPatch = z.infer<typeof coachingProgramPatchSchema>;

/**
 * PUT /admin/coaching/programs/:id/days: the whole list, numbered 1..N in
 * order. A day with media names what it plays: an uploaded track's id for
 * audio, an https url on an allowed host for video (both checked in the service).
 */
export const coachingProgramDaysPutSchema = z
  .object({
    days: z
      .array(
        z.object({
          dayNo: z.number().int().min(1).max(COACHING_PROGRAM_LIMITS.daysMax),
          title: z.string().trim().min(3).max(COACHING_PROGRAM_LIMITS.dayTitleMax),
          lesson: z.string().trim().min(20).max(COACHING_PROGRAM_LIMITS.lessonMax),
          mediaKind: z.enum(COACHING_PROGRAM_MEDIA_KINDS).default('none'),
          mediaRef: z.string().trim().max(2048).nullable().optional(),
          kalaPrompt: z.string().trim().min(10).max(COACHING_PROGRAM_LIMITS.promptMax),
          actionTitle: z.string().trim().min(3).max(COACHING_PROGRAM_LIMITS.actionTitleMax),
          actionMinutes: z
            .number()
            .int()
            .min(1)
            .max(COACHING_PROGRAM_LIMITS.actionMinutesMax)
            .nullable()
            .optional(),
        }),
      )
      .min(1)
      .max(COACHING_PROGRAM_LIMITS.daysMax),
  })
  .superRefine((v, ctx) => {
    v.days.forEach((d, i) => {
      if (d.dayNo !== i + 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['days', i, 'dayNo'],
          message: 'days run 1..N in order',
        });
      }
      if (d.mediaKind !== 'none' && !d.mediaRef) {
        ctx.addIssue({
          code: 'custom',
          path: ['days', i, 'mediaRef'],
          message: 'name the track or the video url',
        });
      }
    });
  });
export type CoachingProgramDaysPut = z.infer<typeof coachingProgramDaysPutSchema>;
